import React from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { secondsToMinutes } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { PLAYER_PROGRAM_METADATA } from 'config/testID';

import { VideoItem } from 'services/data-manager/types';

import useProgramDateStr from 'utils/hooks/useProgramDateStr';
import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import { isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import { ConnectionIndicator } from '../ConnectionIndicator';
import TopOverlayMarkers from '../TopOverlayMarkers';
import styles from './styles';

type Props = {
    isEmbeddedTabletPlayer: boolean;
    videoItem: VideoItem | undefined;
    duration: number | undefined;
    primaryColor: string | undefined;
    secondaryColor: string | undefined;
    isCatchup: boolean | undefined;
    isVod: boolean | undefined;
    startoverButtonVisible: boolean;
    embedded?: boolean;
    isContentLocked: boolean;
    recordingItem?: UseRecordingsHookReturnType['recordingItem'];
    collectionSuspensionActive: boolean;
};

const {
    PROGRAM_DETAILS: { SCREEN_DETAIL_DURATION_MIN },
} = LANGUAGE_KEYS;

function TopMetadataInfo({
    isEmbeddedTabletPlayer,
    videoItem,
    duration,
    primaryColor,
    secondaryColor,
    isCatchup,
    isVod,
    startoverButtonVisible,
    embedded,
    isContentLocked,
    recordingItem,
    collectionSuspensionActive,
}: Props) {
    const { t } = useTranslation();
    const program = videoItem?.originalProgram;
    const date = useProgramDateStr(program);

    if (!videoItem) {
        return null;
    }

    return (
        <View
            style={[
                styles.root,
                isEmbeddedTabletPlayer
                    ? styles.tabletEmbeddedMetadataRow
                    : styles.metadataRow,
            ]}
            accessible={false}
            importantForAccessibility="no-hide-descendants"
            testID={PLAYER_PROGRAM_METADATA}
            {...(isWeb && { tabIndex: -1 })}
            dataSet={{ class: 'metadata-info' }}
        >
            <TopOverlayMarkers
                startTime={videoItem?.startTime}
                endTime={videoItem?.endTime}
                parentalRating={videoItem.parentalRating}
                program={program}
                isCatchup={isCatchup}
                startoverButtonVisible={startoverButtonVisible}
                isContentLocked={isContentLocked}
                backgroundColor={embedded ? secondaryColor : primaryColor}
                recordingItem={recordingItem}
                collectionSuspensionActive={collectionSuspensionActive}
            />
            <Text
                category={ETextCategory.body2}
                color={ETextColor.neutral}
                numberOfLines={1}
            >
                {isVod && duration != null && duration > 0
                    ? `${secondsToMinutes(duration)} ${t(
                          SCREEN_DETAIL_DURATION_MIN,
                      )}`
                    : date}
            </Text>
            <View style={styles.lastMarkerIcon}>
                <ConnectionIndicator />
            </View>
        </View>
    );
}

export default React.memo(TopMetadataInfo);
